import { ChangeEvent, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import DashboardLayout from "parts/LayoutContainers/DashboardLayout";
import DashboardNavbar from "parts/Navbars/DashboardNavbar";
import Footer from "parts/Footer";
import MDBox from "components/MDBox";
import { LanguageContext } from "context";
import { useSessionContext } from "context/SessionContext";
import {
  chatWithDailyCoach,
  createDailyCoachTasks,
  fetchDailyCoachVoice,
  getDailyCoachChatJob,
  getDailyCoachSettings,
  hideGlobalLoader,
  showGlobalLoader,
  updateDailyCoachSettings,
  uploadDailyCoachAvatar,
  deleteDailyCoachAvatar,
  formatDateTime,
  getFutureCard,
  parseFutureCardParam,
  parseFutureBoardError,
  talkFailure,
  type DailyCoach as DailyCoachSettings,
  type DailyCoachStyle,
  type DailyCoachTaskDraft,
} from "@chronospark/shared";
import femaleCoachImage from "assets/images/coach/coach1.png";
import maleCoachImage from "assets/images/coach/coach2.png";

type ChatMessage = {
  role: "coach" | "user";
  content: string;
};

function toLocalDate(offset: number): string {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + offset);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatTargetDate(offset: number, t: (key: string) => string): string {
  return t(offset === 0 ? "DailyCoach.today" : "DailyCoach.tomorrow");
}

function coachImage(coach: DailyCoachSettings): string {
  if (coach.avatar_url) return coach.avatar_url;
  return coach.avatar_key === "male" ? maleCoachImage : femaleCoachImage;
}

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

const DAILY_COACH_JOB_POLL_INTERVAL_MS = 1000;
const DAILY_COACH_JOB_TIMEOUT_MS = 180000;

async function waitForDailyCoachReply(
  accessToken: string,
  jobId: string,
  language: "ja" | "en",
) {
  const deadline = Date.now() + DAILY_COACH_JOB_TIMEOUT_MS;
  while (Date.now() < deadline) {
    const result = await getDailyCoachChatJob(accessToken, jobId, language, { hideGlobalLoader: true });
    if (result.status === "completed" && result.response) return result.response;
    await new Promise((resolve) => window.setTimeout(resolve, DAILY_COACH_JOB_POLL_INTERVAL_MS));
  }
  throw new Error(language === "en" ? "Your coach took too long to reply." : "コーチからの返答に時間がかかっています。もう一度お試しください。");
}

function DailyCoach() {
  const { accessToken } = useSessionContext() as { accessToken?: string };
  const { t, language } = useContext(LanguageContext) as { t: (key: string, options?: object) => string; language: string };
  const dailyCoachLanguage = language === "en" ? "en" : "ja";
  const defaultCoach = useMemo<DailyCoachSettings>(() => ({
    name: t("DailyCoach.defaultName"),
    avatar_key: "female",
    style: "gentle",
  }), [t]);
  const durationOptions = useMemo(() => [
    { value: 30, label: t("DailyCoach.minutes", { count: 30 }) },
    { value: 60, label: t("DailyCoach.oneHour") },
    { value: 120, label: t("DailyCoach.twoHours") },
    { value: 240, label: t("DailyCoach.fourHours") },
    { value: 480, label: t("DailyCoach.eightHours") },
    { value: 720, label: t("DailyCoach.twelveHours") },
  ], [t]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const voiceAudioRef = useRef<HTMLAudioElement | null>(null);
  const voiceUrlRef = useRef<string | null>(null);
  const [coach, setCoach] = useState<DailyCoachSettings>(defaultCoach);
  const [name, setName] = useState(defaultCoach.name);
  const [style, setStyle] = useState<DailyCoachStyle>(defaultCoach.style);
  const [targetOffset, setTargetOffset] = useState(0);
  const [availableMinutes, setAvailableMinutes] = useState(30);
  const [voiceOutput, setVoiceOutput] = useState(false);
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([
    { role: "coach", content: t("DailyCoach.welcome") },
  ]);
  const [sessionId, setSessionId] = useState<string | undefined>();
  const [proposal, setProposal] = useState<DailyCoachTaskDraft[]>([]);
  const [selectedTaskIndexes, setSelectedTaskIndexes] = useState<number[]>([]);
  const [hasWeeklyTasks, setHasWeeklyTasks] = useState<boolean | null>(null);
  const [createAsUnlinked, setCreateAsUnlinked] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [sending, setSending] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [creatingTasks, setCreatingTasks] = useState(false);
  const [needsInitialSetup, setNeedsInitialSetup] = useState(false);
  const [feedback, setFeedback] = useState<{ severity: "success" | "info" | "error"; text: string } | null>(null);
  // 未来ボードのカードから開いたとき（?future_card_id=）。サーバーが、カードの最新の保存版と関連ゴールを使う。
  const [params, setParams] = useSearchParams();
  const futureCardId = parseFutureCardParam(params.get("future_card_id"));
  const [futureCardTitle, setFutureCardTitle] = useState<string | null>(null);
  const [hasGoalContext, setHasGoalContext] = useState<boolean | null>(null);

  const targetDate = useMemo(() => toLocalDate(targetOffset), [targetOffset]);

  useEffect(() => {
    if (!accessToken) {
      setLoadingSettings(false);
      return;
    }

    let active = true;
    getDailyCoachSettings(accessToken, dailyCoachLanguage)
      .then((response) => {
        if (!active) return;
        const nextCoach = { ...defaultCoach, ...response.coach };
        setCoach(nextCoach);
        setName(nextCoach.name);
        setStyle(nextCoach.style);
        setNeedsInitialSetup(response.is_initialized === false);
      })
      .catch(() => {
        if (active) {
          setNeedsInitialSetup(false);
          setFeedback({ severity: "error", text: t("DailyCoach.loadError") });
        }
      })
      .finally(() => {
        if (active) setLoadingSettings(false);
      });

    return () => {
      active = false;
    };
  }, [accessToken, dailyCoachLanguage, t]);

  useEffect(() => {
    setMessages((current) => current.length === 1 && current[0].role === "coach"
      ? [{ role: "coach", content: t("DailyCoach.welcome") }]
      : current);
  }, [t]);

  useEffect(() => {
    setProposal([]);
    setSelectedTaskIndexes([]);
    setHasWeeklyTasks(null);
    setCreateAsUnlinked(false);
  }, [targetDate]);

  // 表示用にカードのタイトルを読む（カードが見つからないときは、通常の開き方に戻す）
  useEffect(() => {
    setFutureCardTitle(null);
    setHasGoalContext(null);
    if (futureCardId === null || !accessToken) return undefined;
    let active = true;
    getFutureCard(accessToken, futureCardId, { hideGlobalLoader: true })
      .then((result) => { if (active) setFutureCardTitle(result.card.title); })
      .catch((error) => {
        if (!active) return;
        const failure = parseFutureBoardError(error);
        if (failure.kind === "not_found") {
          setFeedback({ severity: "info", text: t(failure.messageKey) });
          setParams({}, { replace: true });
        }
      });
    return () => { active = false; };
  }, [futureCardId, accessToken, t, setParams]);

  const clearFutureCard = () => {
    setParams({}, { replace: true });
    setSessionId(undefined);
    setProposal([]);
    setSelectedTaskIndexes([]);
    setHasWeeklyTasks(null);
    setCreateAsUnlinked(false);
  };

  useEffect(() => () => {
    voiceAudioRef.current?.pause();
    if (voiceUrlRef.current) URL.revokeObjectURL(voiceUrlRef.current);
  }, []);

  useEffect(() => {
    if (!voiceOutput) voiceAudioRef.current?.pause();
  }, [voiceOutput]);

  const speak = async (text: string) => {
    if (!voiceOutput || !accessToken) return;

    try {
      voiceAudioRef.current?.pause();
      if (voiceUrlRef.current) URL.revokeObjectURL(voiceUrlRef.current);

      const audioBlob = await fetchDailyCoachVoice(accessToken, text, dailyCoachLanguage, coach.avatar_key);
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      voiceAudioRef.current = audio;
      voiceUrlRef.current = audioUrl;
      audio.onended = () => {
        if (voiceUrlRef.current === audioUrl) {
          URL.revokeObjectURL(audioUrl);
          voiceUrlRef.current = null;
        }
      };
      await audio.play();
    } catch (error) {
      setFeedback({ severity: "error", text: errorMessage(error, t("DailyCoach.voiceError")) });
    }
  };

  const saveSettings = async () => {
    if (!accessToken) return;
    const trimmedName = name.trim();
    if (!trimmedName) {
      setFeedback({ severity: "error", text: t("DailyCoach.missingName") });
      return;
    }

    setSavingSettings(true);
    try {
      const response = await updateDailyCoachSettings(accessToken, {
        name: trimmedName,
        avatar_key: coach.avatar_key,
        style,
      }, dailyCoachLanguage);
      setCoach(response.coach);
      setName(response.coach.name);
      setStyle(response.coach.style);
      setNeedsInitialSetup(false);
      setFeedback({ severity: "success", text: t("DailyCoach.saved") });
    } catch (error) {
      setFeedback({ severity: "error", text: errorMessage(error, t("DailyCoach.genericError")) });
    } finally {
      setSavingSettings(false);
    }
  };

  const selectAvatar = async (avatarKey: "female" | "male") => {
    if (!accessToken) return;
    const nextCoach = { ...coach, avatar_key: avatarKey } as DailyCoachSettings;
    setCoach(nextCoach);
    try {
      const response = await updateDailyCoachSettings(accessToken, { avatar_key: avatarKey }, dailyCoachLanguage);
      setCoach(response.coach);
      setNeedsInitialSetup(false);
    } catch (error) {
      setFeedback({ severity: "error", text: errorMessage(error, t("DailyCoach.genericError")) });
    }
  };

  const uploadAvatar = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !accessToken) return;
    if (file.size > 1024 * 1024) {
      setFeedback({ severity: "error", text: t("DailyCoach.avatarTooLarge") });
      return;
    }

    setSavingSettings(true);
    try {
      const response = await uploadDailyCoachAvatar(accessToken, file, dailyCoachLanguage);
      setCoach(response.coach);
      setNeedsInitialSetup(false);
      setFeedback({ severity: "success", text: t("DailyCoach.avatarSaved") });
    } catch (error) {
      setFeedback({ severity: "error", text: errorMessage(error, t("DailyCoach.genericError")) });
    } finally {
      setSavingSettings(false);
    }
  };

  const deleteAvatar = async () => {
    if (!accessToken || !window.confirm(t("DailyCoach.deleteAvatarConfirm"))) return;

    setSavingSettings(true);
    try {
      const response = await deleteDailyCoachAvatar(accessToken, dailyCoachLanguage);
      setCoach(response.coach);
      setFeedback({ severity: "success", text: t("DailyCoach.avatarDeleted") });
    } catch (error) {
      setFeedback({ severity: "error", text: errorMessage(error, t("DailyCoach.genericError")) });
    } finally {
      setSavingSettings(false);
    }
  };

  const sendMessage = async () => {
    const trimmedMessage = message.trim();
    if (!trimmedMessage || !accessToken) return;

    const userMessage: ChatMessage = { role: "user", content: trimmedMessage };
    setMessages((current) => [...current, userMessage]);
    setMessage("");
    setSending(true);
    setFeedback(null);
    showGlobalLoader();

    try {
      const accepted = await chatWithDailyCoach(accessToken, {
        message: trimmedMessage,
        target_date: targetDate,
        available_minutes: availableMinutes,
        session_id: sessionId,
        language: dailyCoachLanguage,
        async: true,
        ...(futureCardId !== null ? { future_card_id: futureCardId } : {}),
      }, { hideGlobalLoader: true });
      const response = await waitForDailyCoachReply(accessToken, accepted.job_id, dailyCoachLanguage);
      setSessionId(response.session_id);
      setCoach(response.coach);
      setMessages((current) => [...current, { role: "coach", content: response.message }]);
      void speak(response.message);
      const tasks = response.task_proposal?.tasks ?? [];
      setProposal(tasks);
      setSelectedTaskIndexes(tasks.map((_, index) => index));
      setHasWeeklyTasks(response.task_proposal?.has_weekly_tasks ?? null);
      setHasGoalContext(response.task_proposal?.has_goal_context ?? null);
      setCreateAsUnlinked(false);
    } catch (error) {
      if (futureCardId !== null) {
        // 未来ボードから開いたときの拒否（無効・上限・実行中など）は、コードで届く
        const failure = talkFailure(error);
        const zone = "Asia/Tokyo";
        setFeedback({
          severity: "info",
          text: failure.resetsAt ? t("futureBoard.assist.exhausted", { time: formatDateTime(failure.resetsAt, language, zone) }) : t(failure.messageKey),
        });
      } else {
        setFeedback({ severity: "error", text: errorMessage(error, t("DailyCoach.genericError")) });
      }
    } finally {
      setSending(false);
      hideGlobalLoader();
    }
  };

  const createTasks = async () => {
    if (!accessToken || selectedTaskIndexes.length === 0) return;
    const tasks = proposal.filter((_, index) => selectedTaskIndexes.includes(index));
    const requiresUnlinkedConfirmation = tasks.some((task) => !task.source_week_task_id);
    if (requiresUnlinkedConfirmation && !createAsUnlinked) {
      setFeedback({ severity: "info", text: t("DailyCoach.unlinkedConfirmationRequired") });
      return;
    }
    setCreatingTasks(true);
    setFeedback(null);
    try {
      await createDailyCoachTasks(accessToken, {
        target_date: targetDate,
        available_minutes: availableMinutes,
        tasks,
        create_as_unlinked: requiresUnlinkedConfirmation && createAsUnlinked,
        language: dailyCoachLanguage,
      });
      setFeedback({ severity: "success", text: t("DailyCoach.created", { count: tasks.length, day: formatTargetDate(targetOffset, t) }) });
      setProposal([]);
      setSelectedTaskIndexes([]);
    } catch (error) {
      setFeedback({ severity: "error", text: errorMessage(error, t("DailyCoach.genericError")) });
    } finally {
      setCreatingTasks(false);
    }
  };

  const toggleTask = (index: number) => {
    setSelectedTaskIndexes((current) =>
      current.includes(index) ? current.filter((value) => value !== index) : [...current, index]
    );
  };

  const selectedProposalTasks = proposal.filter((_, index) => selectedTaskIndexes.includes(index));
  const requiresUnlinkedConfirmation = selectedProposalTasks.some((task) => !task.source_week_task_id);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Typography variant="h4" fontWeight="bold" mb={0.5}>
          {t("DailyCoach.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          {t("DailyCoach.subtitle")}
        </Typography>

        {!accessToken && <Alert severity="error" sx={{ mb: 2 }}>{t("DailyCoach.loginError")}</Alert>}
        {feedback && <Alert severity={feedback.severity} sx={{ mb: 2 }}>{feedback.text}</Alert>}
        {needsInitialSetup && <Alert severity="info" sx={{ mb: 2 }}>{t("DailyCoach.initialSetupRequired")}</Alert>}
        {futureCardId !== null && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={<Button color="inherit" size="small" onClick={clearFutureCard}>{t("futureBoard.coach.clear")}</Button>}
          >
            {futureCardTitle ? t("futureBoard.coach.banner", { title: futureCardTitle }) : t("futureBoard.coach.bannerNoTitle")}
            <br />
            {t("futureBoard.coach.bannerNote")}
            {hasGoalContext && hasWeeklyTasks && (<><br />{t("futureBoard.coach.linkedGoal")}</>)}
          </Alert>
        )}

        <Stack direction={{ xs: "column", lg: "row" }} spacing={2.5} alignItems="stretch">
          <Card sx={{ width: { lg: 330 }, p: 2.5, alignSelf: "flex-start" }}>
            <Stack spacing={2} alignItems="center">
              <Avatar src={coachImage(coach)} sx={{ width: 112, height: 112 }} />
              <Typography variant="h6">{coach.name}</Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                {t("DailyCoach.coachDescription")}
              </Typography>
            </Stack>
            <Divider sx={{ my: 2 }} />
            <Stack spacing={1.5}>
              <TextField
                label={t("DailyCoach.name")}
                value={name}
                onChange={(event) => setName(event.target.value)}
                size="small"
                inputProps={{ maxLength: 32 }}
              />
              <FormControl
                size="small"
                fullWidth
                sx={{
                  "& .MuiSelect-select": {
                    display: "flex",
                    alignItems: "center",
                    minHeight: "40px !important",
                    py: "10px !important",
                  },
                }}
              >
                <InputLabel id="daily-coach-style-label">{t("DailyCoach.style")}</InputLabel>
                <Select
                  labelId="daily-coach-style-label"
                  label={t("DailyCoach.style")}
                  value={style}
                  onChange={(event) => setStyle(event.target.value as DailyCoachStyle)}
                >
                  <MenuItem value="gentle">{t("DailyCoach.gentle")}</MenuItem>
                  <MenuItem value="normal">{t("DailyCoach.normal")}</MenuItem>
                  <MenuItem value="strict">{t("DailyCoach.strict")}</MenuItem>
                </Select>
              </FormControl>
              <Stack direction="row" spacing={1} justifyContent="center">
                <Button
                  variant={coach.avatar_key !== "male" ? "contained" : "outlined"}
                  onClick={() => selectAvatar("female")}
                  sx={{ color: coach.avatar_key !== "male" ? "common.white" : "text.primary" }}
                >
                  {t("DailyCoach.female")}
                </Button>
                <Button
                  variant={coach.avatar_key === "male" ? "contained" : "outlined"}
                  onClick={() => selectAvatar("male")}
                  sx={{ color: coach.avatar_key === "male" ? "common.white" : "text.primary" }}
                >
                  {t("DailyCoach.male")}
                </Button>
              </Stack>
              <Button component="label" variant="outlined" sx={{ color: "text.primary", "&.Mui-disabled": { color: "text.primary" } }}>
                {t("DailyCoach.uploadAvatar")}
                <input ref={fileInputRef} hidden type="file" accept="image/*" onChange={uploadAvatar} aria-label={t("DailyCoach.uploadAvatar")} />
              </Button>
              {coach.avatar_url && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={deleteAvatar}
                  disabled={savingSettings || !accessToken}
                  sx={{ color: "error.main", "&.Mui-disabled": { color: "error.main", opacity: 0.5 } }}
                >
                  {t("DailyCoach.deleteAvatar")}
                </Button>
              )}
              <Button
                variant="contained"
                onClick={saveSettings}
                disabled={savingSettings || !accessToken}
                sx={{ color: "common.white", "&.Mui-disabled": { color: "common.white" } }}
              >
                {savingSettings ? <CircularProgress size={18} color="inherit" /> : t("DailyCoach.saveSettings")}
              </Button>
            </Stack>
          </Card>

          <Card sx={{ flex: 1, minWidth: 0, p: { xs: 1.5, sm: 2.5 } }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ md: "center" }} mb={2}>
              <Box>
                <Typography variant="h6">{t("DailyCoach.consultTitle", { day: formatTargetDate(targetOffset, t) })}</Typography>
                <Typography variant="caption" color="text.secondary">{t("DailyCoach.taskExplanation")}</Typography>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2">{t("DailyCoach.voice")}</Typography>
                <Switch checked={voiceOutput} onChange={(event) => setVoiceOutput(event.target.checked)} />
              </Stack>
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={1.5}>
              {[0, 1].map((offset) => (
                <Chip key={offset} label={formatTargetDate(offset, t)} color={targetOffset === offset ? "info" : "default"} onClick={() => setTargetOffset(offset)} />
              ))}
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={2}>
              {durationOptions.map((option) => (
                <Chip key={option.value} label={option.label} color={availableMinutes === option.value ? "info" : "default"} onClick={() => setAvailableMinutes(option.value)} />
              ))}
            </Stack>

            <Box sx={{ bgcolor: "grey.50", borderRadius: 2, minHeight: 280, p: 2, mb: 2, overflowY: "auto", maxHeight: 420 }}>
              {messages.map((chatMessage, index) => (
                <Stack key={`${chatMessage.role}-${index}`} direction={chatMessage.role === "coach" ? "row" : "row-reverse"} spacing={1} mb={1.5} alignItems="flex-start">
                  {chatMessage.role === "coach" && <Avatar src={coachImage(coach)} sx={{ width: 36, height: 36 }} />}
                  <Box sx={{ maxWidth: "78%", bgcolor: chatMessage.role === "coach" ? "common.white" : "info.main", color: chatMessage.role === "coach" ? "text.primary" : "primary.contrastText", borderRadius: 2, px: 1.5, py: 1, boxShadow: 1 }}>
                    <Typography variant="body2" whiteSpace="pre-wrap">{chatMessage.content}</Typography>
                  </Box>
                </Stack>
              ))}
              {sending && <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={20} /><Typography variant="body2">{t("DailyCoach.thinking")}</Typography></Stack>}
            </Box>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <TextField
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    void sendMessage();
                  }
                }}
                placeholder={t("DailyCoach.placeholder")}
                multiline
                minRows={2}
                fullWidth
                disabled={sending || !accessToken}
              />
              <Button
                variant="contained"
                onClick={sendMessage}
                disabled={sending || !message.trim() || !accessToken}
                sx={{ minWidth: 100, color: "common.white", "&.Mui-disabled": { color: "common.white" } }}
              >
                {t("DailyCoach.consult")}
              </Button>
            </Stack>

            {proposal.length > 0 && (
              <Box mt={2.5}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="h6" mb={0.5}>{t("DailyCoach.taskProposal", { day: formatTargetDate(targetOffset, t) })}</Typography>
                <Typography variant="body2" color="text.secondary" mb={1.5}>{t("DailyCoach.taskProposalDescription")}</Typography>
                {hasWeeklyTasks === false && (
                  <Alert severity="info" sx={{ mb: 1.5 }}>
                    {futureCardId !== null && hasGoalContext === false ? t("futureBoard.coach.unlinkedGoal") : t("DailyCoach.weeklyTaskUnavailable")}
                  </Alert>
                )}
                <Stack spacing={1}>
                  {proposal.map((task, index) => (
                    <Box key={`${task.title}-${index}`} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5, p: 1 }}>
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        <Checkbox checked={selectedTaskIndexes.includes(index)} onChange={() => toggleTask(index)} />
                        <Box pt={0.75}>
                          <Typography variant="subtitle2">{task.title}</Typography>
                          {task.detail && <Typography variant="body2" color="text.secondary">{task.detail}</Typography>}
                          {task.estimated_duration && <Typography variant="caption" color="text.secondary">{t("DailyCoach.estimate", { duration: task.estimated_duration })}</Typography>}
                          <Typography variant="caption" display="block" color="text.secondary">
                            {task.source_week_task_title
                              ? t("DailyCoach.sourceWeeklyTask", { title: task.source_week_task_title })
                              : t("DailyCoach.unlinkedStep")}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
                {requiresUnlinkedConfirmation && (
                  <Stack
                    component="label"
                    direction="row"
                    spacing={0.5}
                    alignItems="flex-start"
                    sx={{ mt: 1.5, cursor: "pointer" }}
                  >
                    <Checkbox 
                      checked={createAsUnlinked} 
                      onChange={(event) => setCreateAsUnlinked(event.target.checked)} 
                    />
                    <Box pt={1.1}>
                      <Typography variant="body2">
                        {t("DailyCoach.unlinkedConfirmation")}
                      </Typography>
                    </Box>
                  </Stack>
                )}
                <Button
                  sx={{ mt: 2, color: "common.white", "&.Mui-disabled": { color: "common.white" } }}
                  variant="contained"
                  onClick={createTasks}
                  disabled={creatingTasks || selectedTaskIndexes.length === 0 || (requiresUnlinkedConfirmation && !createAsUnlinked)}
                >
                  {creatingTasks ? <CircularProgress size={18} color="inherit" /> : t("DailyCoach.createSelected", { count: selectedTaskIndexes.length })}
                </Button>
              </Box>
            )}
          </Card>
        </Stack>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default DailyCoach;
